import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ConfirmDialogService } from '../../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../../notification/notification.service';
import { SaveWorktimePlanRequest, WorktimePlan, WorktimeService } from '../../api';
import { WT_DAY_HEADS, WT_MONTH_NAMES, wtErrorText, wtSpanLabel } from '../worktime-shared';

// ============================================================================
// Előre jelzett távollétek — „péntekenként 8–12 nem leszek" vagy egy konkrét
// nap / időszak. A nap tényleges bejegyzése mindig felülírja.
// ============================================================================

interface WtPlanVm {
  id: number;
  title: string;
  sub: string;
}

@Component({
  selector: 'app-wt-plans',
  templateUrl: './wt-plans.component.html',
  styleUrl: './wt-plans.component.scss'
})
export class WtPlansComponent implements OnChanges {

  /** Admin: a kiválasztott user; saját nézet: undefined. */
  @Input() userId: number | undefined;
  /** Mentés/törlés után — a naptár újratölt. */
  @Output() changed = new EventEmitter<void>();

  readonly dayNames = WT_DAY_HEADS;
  plans: WtPlanVm[] = [];
  formOpen = false;
  saving = false;

  mode: 'weekly' | 'once' = 'weekly';
  weekday = 5;
  dateFrom = '';
  dateTo = '';
  allDay = false;
  start = '08:00';
  end = '12:00';
  comment = '';

  constructor(
    private worktime: WorktimeService,
    private notification: NotificationService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnChanges(): void {
    this.formOpen = false;
    this.load();
  }

  private load(): void {
    this.worktime.getPlans(this.userId).subscribe({
      next: list => this.plans = (list || []).map(p => this.toVm(p)),
      error: () => this.plans = [],
    });
  }

  private toVm(p: WorktimePlan): WtPlanVm {
    const span = wtSpanLabel(p.startTime, p.endTime);
    const comment = p.comment ? ' · ' + p.comment : '';
    if (p.weekday) {
      const day = (WT_DAY_HEADS[p.weekday - 1] || '').toLowerCase();
      const until = p.dateTo ? ', ' + this.dateLabel(p.dateTo) + '-ig' : '';
      return { id: p.id, title: 'Minden ' + day + ' · ' + span, sub: this.dateLabel(p.dateFrom) + '-tól' + until + comment };
    }
    const range = p.dateTo && p.dateTo !== p.dateFrom
      ? this.dateLabel(p.dateFrom) + ' – ' + this.dateLabel(p.dateTo)
      : this.dateLabel(p.dateFrom);
    return { id: p.id, title: range + ' · ' + span, sub: 'egyszeri' + comment };
  }

  private dateLabel(key: string): string {
    const d = new Date(key + 'T00:00:00');
    return WT_MONTH_NAMES[d.getMonth()].slice(0, 3) + '. ' + d.getDate() + '.';
  }

  private todayKey(): string {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  openForm(): void {
    this.mode = 'weekly';
    this.weekday = 5;
    this.dateFrom = this.todayKey();
    this.dateTo = '';
    this.allDay = false;
    this.start = '08:00';
    this.end = '12:00';
    this.comment = '';
    this.formOpen = true;
  }

  save(): void {
    if (this.saving) { return; }
    if (!this.dateFrom) {
      this.notification.show('Add meg, mikortól érvényes.', 'danger');
      return;
    }
    const request: SaveWorktimePlanRequest = {
      weekday: this.mode === 'weekly' ? Number(this.weekday) : null,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo || null,
      startTime: this.allDay ? null : this.start,
      endTime: this.allDay ? null : this.end,
      comment: this.comment,
    };
    if (this.userId != null) { request.userId = this.userId; }
    this.saving = true;
    this.worktime.savePlan(request).subscribe({
      next: () => {
        this.saving = false;
        this.formOpen = false;
        this.notification.show('Távollét felvéve.', 'success');
        this.load();
        this.changed.emit();
      },
      error: (err) => {
        this.saving = false;
        this.notification.show(wtErrorText(err, 'A távollét mentése nem sikerült.'), 'danger');
      },
    });
  }

  remove(plan: WtPlanVm): void {
    this.confirmDialog.open({
      title: 'Tervezett távollét törlése',
      message: `„${plan.title}" törlődik. A már rögzített napi bejegyzéseket nem érinti.`,
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
      tone: 'danger',
    }).subscribe(result => {
      if (!result?.confirmed) { return; }
      this.worktime.deletePlan(plan.id, this.userId).subscribe({
        next: () => {
          this.load();
          this.changed.emit();
        },
        error: (err) => this.notification.show(wtErrorText(err, 'A törlés nem sikerült.'), 'danger'),
      });
    });
  }
}
